import type { CreateSocialPostRequest, SocialPlatform } from '../../../types/instagramAutomation.types';
import {
    applyTiktokToCreateBody,
    applyYoutubeToCreateBody,
    type TiktokPostFields,
    type YoutubePostFields,
} from '../../../components/InstagramAutomation/platformPostFields';

export type ComposeAspect = '4:5' | '1:1' | '9:16';
export type ComposeFormat = 'image' | 'carousel' | 'reel' | 'tiktok_video' | 'yt_video' | 'yt_short';

export type InstagramComposeResult = {
    platform: SocialPlatform;
    format: ComposeFormat;
    mediaType: 'image' | 'carousel' | 'video';
    aspect: ComposeAspect;
    caption: string;
    title: string;
    description: string;
    hashtags: string[];
    platformOptions: Record<string, unknown>;
    projectId?: string;
    scheduledAt: string;
    /** S3 file keys for publish */
    mediaUrls: string[];
    /** When true, caller should immediately queue for publishing after creating */
    publishNow?: boolean;
};

export type ComposeSlide = { preview: string; file?: File; fileKey?: string };

export const PLATFORM_FORMATS: Record<SocialPlatform, { id: ComposeFormat; label: string }[]> = {
    instagram: [
        { id: 'carousel', label: 'IG Carousel' },
        { id: 'image', label: 'Feed Post' },
        { id: 'reel', label: 'Reel' },
    ],
    tiktok: [{ id: 'tiktok_video', label: 'TikTok Video' }],
    youtube: [
        { id: 'yt_video', label: 'YouTube Video' },
        { id: 'yt_short', label: 'YouTube Short' },
    ],
};

export const PLATFORM_LABEL: Record<SocialPlatform, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

export const ASPECT_CSS: Record<ComposeAspect, string> = {
    '4:5': '4 / 5',
    '1:1': '1 / 1',
    '9:16': '9 / 16',
};

export const isVideoFormat = (f: ComposeFormat) =>
    f === 'reel' || f === 'tiktok_video' || f === 'yt_video' || f === 'yt_short';

export const defaultAspectFor = (f: ComposeFormat): ComposeAspect => (isVideoFormat(f) ? '9:16' : '4:5');

export const maxSlidesFor = (f: ComposeFormat) => (f === 'carousel' ? 10 : 1);

export function mediaTypeFor(f: ComposeFormat, slideCount: number): InstagramComposeResult['mediaType'] {
    if (isVideoFormat(f)) return 'video';
    return f === 'carousel' && slideCount >= 2 ? 'carousel' : 'image';
}

/** Platform-specific fields → calendar `platform_options` (+ YouTube title/description/tags lifted out). */
export function buildPlatformFields(
    platform: SocialPlatform,
    caption: string,
    yt: YoutubePostFields,
    tt: TiktokPostFields,
): { title: string; description: string; hashtags: string[]; platformOptions: Record<string, unknown> } {
    const base = { platform } as CreateSocialPostRequest;
    if (platform === 'youtube') {
        const { platform: _p, title, description, tags, ...options } = applyYoutubeToCreateBody(base, yt);
        return { title: title || '', description: description || '', hashtags: tags || [], platformOptions: options };
    }
    const options: Record<string, unknown> = platform === 'tiktok' ? { ...applyTiktokToCreateBody(base, tt) } : {};
    delete options.platform;
    return { title: caption.slice(0, 48), description: caption, hashtags: [], platformOptions: options };
}

/** Returns an error message when platform-required fields are missing. */
export function validatePlatformFields(
    platform: SocialPlatform,
    yt: YoutubePostFields,
    tt: TiktokPostFields,
): string | null {
    if (platform === 'youtube' && !yt.title.trim()) return 'YouTube title is required';
    if (platform === 'tiktok') {
        if (!tt.tiktok_privacy_level) return 'Choose a TikTok privacy level';
        if (!tt.tiktok_publish_consent_accepted) return 'Accept TikTok publish consent to continue';
    }
    return null;
}
